import { Maybe } from "@/domains/common/types";
import { withDexieErrorHandling } from "@/domains/db/dbErrorHandling";
import { Uuid } from "@/domains/global/identifiers";
import { logger } from "@/modules/logger";
import { AppStore } from "@/store/AppStore";
import { BaseSyncModel } from "@/store/sync/BaseSyncModel";
import {
  SyncModelData,
  SyncUpdate,
  SyncUpdateValue,
  SyncModelKind,
  OptimisticSyncUpdate,
  SerializedOptimisticUpdate,
} from "@/store/sync/types";
import { AppSubStore, AppSubStoreArgs } from "@/store/types";
import { liveQuery, Table } from "dexie";
import {
  ObservableMap,
  ObservableSet,
  action,
  computed,
  makeObservable,
  observable,
  onBecomeObserved,
  onBecomeUnobserved,
  runInAction,
} from "mobx";
import { computedFn, ILazyObservable, lazyObservable } from "mobx-utils";
import { Subscription } from "node_modules/react-hook-form/dist/utils/createSubject";

const BULK_RECOMPUTE_BATCH_SIZE = 100000;

export abstract class BaseSyncModelStore<
  SyncModel extends BaseSyncModel<ModelData>,
  ModelData extends SyncModelData,
> extends AppSubStore {
  public pool: ObservableMap<Uuid, WeakRef<SyncModel> | undefined> = new ObservableMap();

  public ids: ObservableSet<Uuid> = new ObservableSet();
  public idsLoaded: boolean = false;
  public idsSubscription: Maybe<Subscription>;
  public modelKind: SyncModelKind;

  private bulkRemoteUpdatesPool: SyncUpdateValue<SyncModelData>[] = [];
  public constructor({
    modelKind,
    ...injectedDeps
  }: { modelKind: SyncModelKind } & AppSubStoreArgs) {
    super(injectedDeps);
    this.modelKind = modelKind;

    makeObservable<
      this,
      | "loadModel"
      | "loadModels"
      | "createLazyObservable"
      | "createLazyObservableMany"
      | "bulkRemoteUpdatesPool"
      | "grantAccessToModelIfItExistsLocally"
      | "revokeAccessToModelIfItExistsLocally"
    >(this, {
      computeIndexes: true,
      getAsync: true,
      getManyAsync: true,
      createSyncModel: false,

      idsSubscription: observable,
      idsLoaded: observable,
      ids: observable,
      all: computed,
      unsubscribeFromIds: action,
      subscribeToIds: action,
      has: false,
      doesNotExist: false,

      createLazyObservable: true,
      createLazyObservableMany: true,
      db: computed,
      modelKind: observable,

      get: false,
      getMany: false,
      pool: observable,
      loadModel: action,
      loadModels: action,

      remoteTable: computed,
      localTable: computed,
      processSyncUpdate: action,
      recompute: action,
      bulkRecompute: action,
      bulkRecomputeAll: action,
      updateObservable: action,

      resetSync: action,
      flushRemote: action,
      bulkRemoteUpdatesPool: false,
      dryProcessSyncUpdate: true,
      grantAccessToModelIfItExistsLocally: false,
      revokeAccessToModelIfItExistsLocally: false,
    });

    onBecomeObserved(this, "ids", () => this.subscribeToIds());
    onBecomeUnobserved(this, "ids", () => this.unsubscribeFromIds());
  }

  public subscribeToIds() {
    this.idsSubscription?.unsubscribe();
    this.idsSubscription = liveQuery(() => this.localTable.toCollection().primaryKeys()).subscribe({
      next: ids =>
        runInAction(() => {
          this.ids.replace(ids);
          this.idsLoaded = true;
        }),
    });
  }

  public unsubscribeFromIds() {
    this.idsSubscription?.unsubscribe();
    runInAction(() => (this.idsLoaded = false));
  }

  public get db() {
    return this.store.memDb;
  }

  public doesNotExist = (id: string): boolean => {
    // If ids are not loaded yet, return false because we don't know yet
    // NOTE: this.has(id) needs to be called first to trigger onBecomeObserved
    const hasId = this.has(id);
    if (!this.idsLoaded) return false;
    return !hasId;
  };

  abstract createSyncModel(updateValue: SyncUpdateValue<ModelData>): SyncModel;

  public get remoteTable() {
    return this.db.mappedTables[this.modelKind].remote as Table<SyncUpdateValue<ModelData>>;
  }

  public get localTable() {
    return this.db.mappedTables[this.modelKind].local as Table<SyncUpdateValue<ModelData>>;
  }

  public has = computedFn((id: Uuid): boolean => {
    return this.ids.has(id);
  });

  public get all() {
    // DO NOT USE UNLESS NECESSARY
    const ids = Array.from(this.ids.values());
    return this.getMany(ids);
  }

  public get = computedFn((id: Uuid): SyncModel | undefined => {
    const model = this.pool.get(id)?.deref();
    if (model) return model;
    return this.createLazyObservable(id).current();
  });

  public getMany = computedFn((ids: Uuid[]): SyncModel[] => {
    return this.createLazyObservableMany(ids).current() || [];
  });

  private createLazyObservable(id: Uuid): ILazyObservable<SyncModel | undefined> {
    return lazyObservable((sink: (value: SyncModel | undefined) => void) =>
      this.loadModel(id, sink)
    );
  }

  private createLazyObservableMany(ids: Uuid[]): ILazyObservable<SyncModel[]> {
    return lazyObservable((sink: (value: SyncModel[]) => void) =>
      this.loadModels(ids, sink)
    ) as ILazyObservable<SyncModel[]>;
  }

  public async getAsync(id: Uuid): Promise<SyncModel | undefined> {
    const existing = this.pool.get(id)?.deref();
    if (existing) return existing;

    const data = await this.localTable.get(id);
    if (!data) return undefined;

    const model = this.createSyncModel(data);
    runInAction(() => this.pool.set(id, new WeakRef(model)));
    return model;
  }

  public async getManyAsync(ids: Uuid[]): Promise<SyncModel[]> {
    const models: SyncModel[] = [];
    for (const id of ids) {
      const model = await this.getAsync(id);
      if (model) models.push(model);
    }
    return models;
  }

  private async loadModel(id: Uuid, sink: (value: SyncModel | undefined) => void) {
    if (id === undefined) return;
    const data = await this.localTable.get(id);
    if (!data) {
      sink(undefined);
      return;
    }
    const model = this.createSyncModel(data);
    runInAction(() => this.pool.set(id, new WeakRef(model)));
    sink(model);
  }

  private async loadModels(ids: Uuid[], sink: (value: SyncModel[]) => void) {
    const models: SyncModel[] = [];
    const idsToLoad: Uuid[] = [];

    for (const id of ids) {
      const existingModel = this.pool.get(id)?.deref();
      if (existingModel) models.push(existingModel);
      else idsToLoad.push(id);
    }

    if (idsToLoad.length > 0) {
      const data = await this.localTable.bulkGet(idsToLoad);
      runInAction(() => {
        data.forEach((item, index) => {
          if (item) {
            const model = this.createSyncModel(item);
            const id = idsToLoad[index];
            this.pool.set(id, new WeakRef(model));
            models.push(model);
          }
        });
      });
    }

    sink(models);
  }

  public async processSyncUpdate(
    update: SyncUpdate<ModelData>,
    _options: { hydrating?: boolean } = {}
  ) {
    switch (update.kind) {
      case "ACL_UPSERTED":
      case "UPSERTED": {
        const updateValue = update.value as SyncUpdateValue<ModelData>;
        // in case model was re-shared with us
        this.grantAccessToModelIfItExistsLocally(updateValue.model_id);

        // DEXIE REFACTOR TODO: Check model version first
        await withDexieErrorHandling(
          async () => {
            await this.remoteTable?.put(updateValue, updateValue.model_id);
          },
          this.store,
          `[BaseSyncModelStore] processSyncUpdate`
        );
        break;
      }
      case "DELETED": {
        const modelId = update.value.model_id;
        await this.remoteTable?.delete(modelId);
        break;
      }
      case "ACL_REVOKED": {
        const modelId = update.value.model_id;
        this.revokeAccessToModelIfItExistsLocally(modelId);
        await this.remoteTable?.delete(modelId);
        break;
      }
    }

    await this.recompute(update.value.model_id);
  }

  // TODO: relator to make it reusable with regular processSyncUpdate
  public dryProcessSyncUpdate(update: SyncUpdate<ModelData>): boolean {
    switch (update.kind) {
      case "ACL_UPSERTED":
      case "UPSERTED": {
        // DEXIE REFACTOR TODO: Check model version first
        const value = update.value as SyncUpdateValue<ModelData>;
        this.bulkRemoteUpdatesPool.push(value);
        return true;
      }
      case "DELETED":
      case "ACL_REVOKED": {
        // noop
        return false;
      }
    }

    return false;
  }

  public resetSync() {
    this.pool.clear();
    // TODO: Reset Dexie tables?
  }

  public computeIndexes(_params: {
    store: AppStore;
    remoteData: Maybe<SyncUpdateValue<ModelData>>;
    optimisticUpdates: OptimisticSyncUpdate<ModelData>[];
  }): Record<string, unknown> {
    return {};
  }

  public async recompute(modelId: Uuid) {
    const remoteData = await this.remoteTable.get(modelId);
    const optimisticUpdates = await this.db.queue.optimisticUpdates
      .where({ model_id: modelId })
      .sortBy("locally_committed_at");

    const lastOptimisticUpdate = optimisticUpdates.at(-1);
    if (lastOptimisticUpdate?.kind === "DELETED" || lastOptimisticUpdate?.kind === "ACL_REVOKED") {
      logger.debug({
        message: `[BASE SYNC MODEL STORE] deleting / revoking access`,
        info: { modelId, modelKind: this.modelKind },
      });

      await this.localTable.delete(modelId);
      runInAction(() => this.pool.delete(modelId));
      return;
    }

    const data = lastOptimisticUpdate?.value || remoteData;
    if (!data) {
      // Case: remoteData is deleted and there are no optimistic updates\
      await this.localTable.delete(modelId);
      runInAction(() => this.pool.delete(modelId));
      return;
    }

    const indexes = this.computeIndexes({ store: this.store, remoteData, optimisticUpdates });

    const dataWithIndexes: SyncUpdateValue<ModelData> & Record<string, unknown> = {
      ...(data as SyncUpdateValue<ModelData>),
      ...indexes,
    };

    await withDexieErrorHandling(
      async () => {
        await this.localTable.put(dataWithIndexes, dataWithIndexes.model_id);
      },
      this.store,
      `[BaseSyncModelStore] recompute`
    );

    // this is a noop update to retrigger get for observables that previously returned undefined in the case they they become defined through the recompute
    if (!this.pool.has(modelId)) {
      runInAction(() => this.pool.set(modelId, undefined));
      runInAction(() => this.pool.delete(modelId));
    }
  }

  public async bulkRecomputeAll() {
    const modelIds = await this.remoteTable.orderBy("model_id").primaryKeys();
    const batchSize = Math.min(modelIds.length, BULK_RECOMPUTE_BATCH_SIZE);
    for (let i = 0; i < modelIds.length; i += batchSize) {
      const batchModelIds = modelIds.slice(i, i + batchSize);
      await this.bulkRecompute(batchModelIds);
    }
  }

  public async bulkRecompute(modelIds: Uuid[]) {
    const remoteData = await this.remoteTable.bulkGet(modelIds);

    const allOptimisticUpdates = await this.db.queue.optimisticUpdates
      .where("model_id")
      .anyOf(modelIds)
      .sortBy("locally_committed_at");

    const optimisticUpdatesById = new Map<Uuid, SerializedOptimisticUpdate<SyncModelData>[]>();
    for (const update of allOptimisticUpdates) {
      optimisticUpdatesById.set(update.model_id, [
        ...(optimisticUpdatesById.get(update.model_id) || []),
        update,
      ]);
    }

    const toDelete: Uuid[] = [];
    const toPut: (SyncUpdateValue<ModelData> & Record<string, unknown>)[] = [];

    for (const [i, modelId] of modelIds.entries()) {
      const optimisticUpdates = optimisticUpdatesById.get(modelId) || [];
      const lastOptimisticUpdate = optimisticUpdates.at(-1);

      if (
        lastOptimisticUpdate?.kind === "DELETED" ||
        lastOptimisticUpdate?.kind === "ACL_REVOKED"
      ) {
        logger.debug({
          message: `[BASE SYNC MODEL STORE] deleting / revoking access`,
          info: { modelId, modelKind: this.modelKind },
        });

        toDelete.push(modelId);
        continue;
      }

      const data = lastOptimisticUpdate?.value || remoteData[i];
      if (!data) {
        // Case: remoteData is deleted and there are no optimistic updates\
        toDelete.push(modelId);
        continue;
      }

      const indexes = this.computeIndexes({
        store: this.store,
        remoteData: remoteData[i],
        optimisticUpdates,
      });

      const dataWithIndexes: SyncUpdateValue<ModelData> & Record<string, unknown> = {
        ...(data as SyncUpdateValue<ModelData>),
        ...indexes,
      };

      toPut.push(dataWithIndexes);
    }

    await withDexieErrorHandling(
      () =>
        this.db.transaction("rw", this.localTable, async () => {
          await this.localTable.bulkDelete(toDelete);
          await this.localTable.bulkPut(toPut);
        }),
      this.store,
      `[BaseSyncModelStore] bulkRecompute`
    );

    runInAction(() => {
      for (const modelId of toDelete) {
        this.pool.delete(modelId);
      }
      // this is a noop update to retrigger get for observables that previously returned undefined in the case they they become defined through the recompute

      for (const data of toPut) {
        const modelId = data.model_id;
        if (!this.pool.has(modelId)) {
          this.pool.set(modelId, undefined);
          this.pool.delete(modelId);
        }
      }
    });
  }

  async updateObservable(modelId: Uuid) {
    // Only update if there is an observable in memory
    const existing = this.pool.get(modelId)?.deref();
    if (existing) await Promise.all([existing.updateFromLocal(), existing.updateFromRemote()]);
  }

  public async flushRemote() {
    const values = this.bulkRemoteUpdatesPool as SyncUpdateValue<ModelData>[];
    await withDexieErrorHandling(
      async () => {
        await this.remoteTable.bulkPut(values);
      },
      this.store,
      `[BaseSyncModelStore] flushRemote`
    );
    this.bulkRemoteUpdatesPool = [];
  }

  private grantAccessToModelIfItExistsLocally(modelId: Uuid) {
    const model = this.get(modelId);
    model?.setCanAccess(true);
  }

  private revokeAccessToModelIfItExistsLocally(modelId: Uuid) {
    const model = this.get(modelId);
    model?.setCanAccess(false);
  }
}
